/**
 * Number theory tables, built once at startup.
 *
 * Everything the game asks about an integer (is it prime? which prime is it? how does it
 * factor?) is a table lookup, so a tap never does more than O(1) math work.
 */

/** Largest integer any horizon can reach, plus headroom for the number-line lookahead. */
export const SIEVE_LIMIT = 100_000 + 512;

/** Smallest prime factor of n (0 for n < 2). spf[n] === n means n is prime. */
const spf = new Uint32Array(SIEVE_LIMIT + 1);
/** primeCount[n] = π(n), the number of primes ≤ n. */
const primeCount = new Uint32Array(SIEVE_LIMIT + 1);
/** twinCount[n] = number of twin pairs (p, p+2) with p+2 ≤ n. */
const twinCount = new Uint32Array(SIEVE_LIMIT + 1);

for (let i = 2; i <= SIEVE_LIMIT; i++) {
  if (spf[i] !== 0) continue;
  spf[i] = i;
  for (let j = i * i; j <= SIEVE_LIMIT; j += i) {
    if (spf[j] === 0) spf[j] = i;
  }
}
for (let i = 2; i <= SIEVE_LIMIT; i++) {
  const p = spf[i] === i ? 1 : 0;
  primeCount[i] = primeCount[i - 1]! + p;
  twinCount[i] = twinCount[i - 1]! + (p && i >= 5 && spf[i - 2] === i - 2 ? 1 : 0);
}

function inRange(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= SIEVE_LIMIT;
}

export function isPrime(n: number): boolean {
  return inRange(n) && n >= 2 && spf[n] === n;
}

/** n is the upper member of a twin pair, i.e. both n-2 and n are prime. */
export function completesTwin(n: number): boolean {
  return isPrime(n) && isPrime(n - 2);
}

export function isTwinPrime(n: number): boolean {
  return isPrime(n) && (isPrime(n - 2) || isPrime(n + 2));
}

/** π(n): how many primes are ≤ n. Zero for n < 2. */
export function primesUpTo(n: number): number {
  if (n < 2) return 0;
  return primeCount[Math.min(n, SIEVE_LIMIT)]!;
}

/** Number of twin pairs (p, p+2) fully contained in [0, n]. */
export function twinPairsUpTo(n: number): number {
  if (n < 5) return 0;
  return twinCount[Math.min(n, SIEVE_LIMIT)]!;
}

/** 1-based index of a prime: primeIndex(2) === 1, primeIndex(97) === 25. */
export function primeIndex(p: number): number {
  return isPrime(p) ? primeCount[p]! : 0;
}

/** Nearest prime strictly after `from` when walking in `dir`, or null if none is reachable. */
export function nextPrime(from: number, dir: 1 | -1): number | null {
  if (dir === 1) {
    for (let n = Math.max(from + 1, 2); n <= SIEVE_LIMIT; n++) if (spf[n] === n) return n;
  } else {
    for (let n = Math.min(from - 1, SIEVE_LIMIT); n >= 2; n--) if (spf[n] === n) return n;
  }
  return null;
}

/** All primes in [lo, hi], ascending. */
export function primesBetween(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let n = Math.max(2, lo); n <= Math.min(hi, SIEVE_LIMIT); n++) {
    if (spf[n] === n) out.push(n);
  }
  return out;
}

/** Prime factorisation as [prime, exponent] pairs, e.g. 360 → [[2,3],[3,2],[5,1]]. */
export function factorize(n: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let m = Math.abs(n);
  if (!inRange(m) || m < 2) return out;
  while (m > 1) {
    const p = spf[m]!;
    let e = 0;
    while (m % p === 0) {
      m /= p;
      e++;
    }
    out.push([p, e]);
  }
  return out;
}

const SUPERSCRIPT = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/** Human-readable factorisation: 360 → "2³ · 3² · 5", -12 → "−1 · 2² · 3". */
export function formatFactors(n: number): string {
  const parts = factorize(n).map(([p, e]) =>
    e === 1 ? `${p}` : `${p}${[...String(e)].map((d) => SUPERSCRIPT[Number(d)]).join('')}`,
  );
  if (n < 0) parts.unshift('−1');
  return parts.join(' · ');
}
